import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Clock, Users, TrendingUp, Download, Activity } from 'lucide-react';
import { useAdmin, AnalyticsData } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Analytics = () => {
  const { isAdmin, fetchAnalytics } = useAdmin();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    if (isAdmin) {
      loadAnalytics();
    }
  }, [isAdmin, timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await fetchAnalytics(timeRange);
      setAnalyticsData(data);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  };

  const exportAnalytics = () => {
    const csvContent = [
      ['Page URL', 'Page Views', 'Unique Visitors', 'Avg Duration (seconds)'].join(','),
      ...analyticsData.map(item => [
        item.page_url,
        item.page_visits,
        item.unique_visitors,
        item.average_duration
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chesshatch-analytics-${timeRange}days-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalViews = analyticsData.reduce((sum, item) => sum + item.page_visits, 0);
  const totalUniqueVisitors = analyticsData.reduce((sum, item) => sum + item.unique_visitors, 0);
  const avgDuration = analyticsData.length > 0 ? 
    analyticsData.reduce((sum, item) => sum + item.average_duration, 0) / analyticsData.length : 0;

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Website Analytics</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-20"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-16 mb-2"></div>
                <div className="h-3 bg-muted rounded w-24"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Website Analytics</h1>
          <p className="text-muted-foreground">Track visitor behavior and page performance</p>
          <div className="flex items-center gap-2 mt-2">
            <Activity className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-muted-foreground">Enhanced user behavior tracking active</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md">
            {[7, 30, 90].map((days) => (
              <Button
                key={days}
                variant={timeRange === days ? "default" : "ghost"}
                size="sm"
                onClick={() => setTimeRange(days)}
                className="rounded-none first:rounded-l-md last:rounded-r-md"
              >
                {days}d
              </Button>
            ))}
          </div>
          <Button onClick={exportAnalytics} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button onClick={loadAnalytics}>Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Page Views
            </CardTitle>
            <Eye className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalViews.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Last {timeRange} days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unique Visitors
            </CardTitle>
            <Users className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUniqueVisitors.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Unique sessions tracked
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg. Duration
            </CardTitle>
            <Clock className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDuration(avgDuration)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Time per page view
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Page Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page URL</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead className="text-right">Unique Visitors</TableHead>
                <TableHead className="text-right">Avg Duration</TableHead>
                <TableHead className="text-right">Engagement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analyticsData.map((item, index) => {
                // Calculate proper engagement score (0-100%)
                const calculateEngagementScore = (): number => {
                  // Factor 1: Time spent (0-1 scale, 5 minutes = max engagement)
                  const durationFactor = Math.min(item.average_duration / 300, 1);
                  
                  // Factor 2: Repeat visits (0-1 scale, 5 visits/user = max)
                  const visitsPerUser = item.unique_visitors > 0 
                    ? item.page_visits / item.unique_visitors 
                    : 1;
                  const repeatFactor = Math.min((visitsPerUser - 1) / 4, 1);
                  
                  // Weighted: 60% duration, 40% repeat visits
                  return (durationFactor * 0.6 + repeatFactor * 0.4) * 100;
                };
                
                const engagementScore = Math.round(calculateEngagementScore());
                
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="font-medium">{item.page_url}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-medium">{item.page_visits.toLocaleString()}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.unique_visitors.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatDuration(item.average_duration)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={
                        engagementScore > 70 ? "default" : 
                        engagementScore > 40 ? "secondary" : "destructive"
                      }>
                        {engagementScore}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};